import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import FormModal from '@/components/v2/molecules/admin/shared/FormModal';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

import {
  BusinessExpense,
  useCreateBusinessExpense,
  useUpdateBusinessExpense,
} from "@/hooks/useBusinessExpenses";

import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { filterActiveContracts } from "@/utils/contractUtils";

import {
  useBusinessExpenseSubtypes,
  useBusinessExpenseTypes,
} from "@/hooks/data/useEntityCategories";

import CustomDatePicker from "@/components/CustomDatePicker";
import { BUSINESS_PAYMENT_TYPES } from "@/constants/businessExpenses";
import { useImageUpload } from "@/hooks/useImageUpload";

import {
  createBusinessExpenseSchema,
  type CreateBusinessExpenseData,
} from "@/schemas/admin/BusinessExpensesTab/createBusinessExpenseSchema";

import { format } from "date-fns";
import { parseDateFromDatabase } from "@/utils/dateParser";

interface BusinessExpenseFormModalProps {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  editingExpense: BusinessExpense | null;
}

const BusinessExpenseFormModal = ({
  isOpen,
  onClose,
  editingExpense,
}: BusinessExpenseFormModalProps) => {
  const [isPropertyOpen, setIsPropertyOpen] = useState(false);
  const { data: allProperties = [], error: propertiesError } = useProperties();
  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();
  const { data: expenseTypes = [], error: expenseTypesError } =
    useBusinessExpenseTypes();

  const createExpense = useCreateBusinessExpense();
  const updateExpense = useUpdateBusinessExpense();

  const {
    images,
    uploading: uploadingImages,
    addImages,
    removeImage,
    uploadImages,
    reset: resetImages,
  } = useImageUpload();

  const defaultValues: CreateBusinessExpenseData = {
    organization_id: "",
    property_id: "",
    expense_type_id: "",
    expense_subtype_id: "",
    expense_description: "",
    payment_type: "Transferencia",
    total_payment: undefined,
    payment_date: new Date(),
    receipt_url: "",
  };

  const form = useForm<CreateBusinessExpenseData>({
    resolver: zodResolver(createBusinessExpenseSchema),
    defaultValues,
    mode: "onChange",
  });

  const isDisabled =
    !form.formState.isValid ||
    createExpense.isPending ||
    updateExpense.isPending ||
    uploadingImages;

  const selectedTypeId = useWatch({
    control: form.control,
    name: "expense_type_id",
  });

  const selectedOrganizationId = useWatch({
    control: form.control,
    name: "organization_id" as any,
  });

  const { data: expenseSubtypes = [], error: expenseSubtypesError } =
    useBusinessExpenseSubtypes(selectedTypeId);

  // Filter properties by selected organization
  const filteredProperties = useMemo(() => {
    if (!selectedOrganizationId) {
      const activePropertyIds = new Set(
        filterActiveContracts(contracts).map(c => c.property_id)
      );
      return allProperties.filter(p => activePropertyIds.has(p.id));
    }

    const activeContracts = filterActiveContracts(contracts).filter(
      (contract) => contract.organization_id === selectedOrganizationId
    );

    const propertyIds = activeContracts.map((contract) => contract.property_id);
    return allProperties.filter((property) => propertyIds.includes(property.id));
  }, [selectedOrganizationId, allProperties, contracts]);

  const getPropertyName = (propertyId: string) => {
    const property = allProperties.find(p => p.id === propertyId);
    return property?.shortName || property?.title || propertyId;
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const onSubmit = async (data: CreateBusinessExpenseData) => {
    try {
      let receiptUrl = data.receipt_url || "";

      if (images.length > 0) {
        const uploadedUrls = await uploadImages();
        receiptUrl = uploadedUrls[0] || "";
      }

      const payload: any = {
        organization_id: (data as any).organization_id || null,
        property_id: data.property_id || null,
        expense_type_id: data.expense_type_id,
        expense_subtype_id: data.expense_subtype_id,
        expense_description: data.expense_description || null,
        payment_type:
          data.payment_type as (typeof BUSINESS_PAYMENT_TYPES)[number],
        total_payment: data.total_payment,
        payment_date: data.payment_date
          ? format(data.payment_date as Date, "yyyy-MM-dd")
          : undefined,
        receipt_url: receiptUrl || null,
      };

      if (editingExpense) {
        await updateExpense.mutateAsync({
          id: editingExpense.id,
          ...payload,
        });
      } else {
        await createExpense.mutateAsync(payload);
      }

      form.reset();
      resetImages();
      onClose(false);
    } catch (error) {
      console.error("Error saving business expense:", error);
    }
  };

  useEffect(() => {
    if (editingExpense && isOpen) {
      form.reset({
        organization_id: (editingExpense as any).organization_id ?? "",
        property_id: editingExpense.property_id ?? "",
        expense_type_id: editingExpense.expense_type_id ?? "",
        expense_subtype_id: editingExpense.expense_subtype_id ?? "",
        expense_description: editingExpense.expense_description ?? "",
        payment_type: editingExpense.payment_type ?? "Transferencia",
        total_payment: editingExpense.total_payment ?? 0,
        payment_date: editingExpense.payment_date
          ? parseDateFromDatabase(editingExpense.payment_date) ?? undefined
          : undefined,
        receipt_url: editingExpense.receipt_url ?? "",
      } as any);
    }
  }, [editingExpense, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      form.reset(defaultValues);
      resetImages();
    }
  }, [isOpen]);

  // Reset property when organization changes
  useEffect(() => {
    if (selectedOrganizationId) {
      form.setValue("property_id" as any, "");
    }
  }, [selectedOrganizationId]);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title={editingExpense ? "Editar Egreso del Negocio" : "Nuevo Egreso del Negocio"}
      form={form}
      onSubmit={onSubmit}
      isLoading={isDisabled}
      submitLabel={editingExpense
        ? updateExpense.isPending
          ? "Guardando..."
          : "Guardar Cambios"
        : createExpense.isPending
          ? "Creando..."
          : "Crear Egreso"}
      maxWidth="max-w-md"
    >
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Organización {!editingExpense && "*"}
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar organización" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="property_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Propiedad</FormLabel>
              <Popover open={isPropertyOpen} onOpenChange={setIsPropertyOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isPropertyOpen}
                      className="w-full justify-between"
                      disabled={!selectedOrganizationId}
                    >
                      {field.value
                        ? getPropertyName(field.value)
                        : "Buscar y seleccionar propiedad..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar propiedad..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron propiedades.</CommandEmpty>
                      <CommandGroup>
                        {propertiesError ? (
                          <CommandItem disabled>
                            Error al cargar propiedades
                          </CommandItem>
                        ) : (
                          filteredProperties.map((property) => (
                            <CommandItem
                              key={property.id}
                              value={property.shortName}
                              onSelect={() => {
                                field.onChange(property.id);
                                setIsPropertyOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  field.value === property.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {property.shortName}
                            </CommandItem>
                          ))
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {!selectedOrganizationId && (
                <p className="text-xs text-muted-foreground">
                  Selecciona una organización primero
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="expense_type_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Egreso *</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.setValue("expense_subtype_id", "");
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {expenseTypesError ? (
                      <SelectItem value="" disabled>
                        Error al cargar tipos de egreso
                      </SelectItem>
                    ) : (
                      expenseTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="expense_subtype_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Subtipo de Egreso *</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={!selectedTypeId}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar subtipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {expenseSubtypesError ? (
                      <SelectItem value="" disabled>
                        Error al cargar subtipos de egreso
                      </SelectItem>
                    ) : (
                      expenseSubtypes.map((subtype) => (
                        <SelectItem key={subtype.id} value={subtype.id}>
                          {subtype.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="expense_description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descripción del Egreso</FormLabel>
              <FormControl>
                <Input placeholder="Descripción" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="total_payment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pago Total *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value === "" ? undefined : parseFloat(e.target.value))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="payment_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Pago *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo de pago" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {BUSINESS_PAYMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="payment_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Fecha de Pago</FormLabel>
              <FormControl>
                <CustomDatePicker
                  selected={field.value}
                  onSelect={field.onChange}
                  placeholder="Seleccionar fecha"
                  className="w-full h-fit"
                  isAdminContext
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">
              Comprobante de Pago
            </label>

            <Button
              type="button"
              variant="outline"
              onClick={handleFileClick}
            >
              Seleccionar archivo
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  addImages(files);
                }
              }}
              className="hidden"
            />

            {editingExpense?.receipt_url && images.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Comprobante actual cargado. Selecciona una nueva imagen para
                reemplazarlo.
              </p>
            )}

            {images.length > 0 && (
              <div className="mt-4 space-y-2">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 border rounded-md"
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={image.preview}
                        alt="Preview"
                        className="w-12 h-12 object-cover rounded"
                      />
                      <span className="text-sm text-muted-foreground">
                        {image.uploading ? "Subiendo..." : image.file.name}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="text-destructive hover:text-destructive/80 mr-4"
                      disabled={image.uploading}
                    >
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export default BusinessExpenseFormModal;
